/**
 * Include your custom JavaScript here.
 *
 * We also offer some hooks so you can plug your own logic. For instance, if you want to be notified when the variant
 * changes on product page, you can attach a listener to the document:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   var variant = event.detail.variant; // Gives you access to the whole variant details
 * });
 *
 * You can also add a listener whenever a product is added to the cart:
 *
 * document.addEventListener('product:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 *   var quantity = event.detail.quantity; // Get the quantity that was added
 * });
 *
 * If you are an app developer and requires the theme to re-render the mini-cart, you can trigger your own event. If
 * you are adding a product, you need to trigger the "product:added" event, and make sure that you pass the quantity
 * that was added so the theme can properly update the quantity:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('product:added', {
 *   bubbles: true,
 *   detail: {
 *     quantity: 1
 *   }
 * }));
 *
 * If you just want to force refresh the mini-cart without adding a specific product, you can trigger the event
 * "cart:refresh" in a similar way (in that case, passing the quantity is not necessary):
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 */

// =====================================================================================================
// Thread and wool colours (DMC, Appletons, Robison-Anton)
//   <colour-finder>  product pages: the searchable swatch grid in place of the theme's image swatches.
//                    Markup: snippets/colour-finder.liquid
//   <colour-chart>   "All colours" pages: search every colour of a brand, filter by range, add one to the
//                    basket (DMC, Appletons) or open its listing (Robison-Anton). Markup: snippets/colour-chart.liquid
//   <colour-pattern> inside the chart: paste a pattern key and add every colour on it in one go.
//
// Search rules, shared by the finder and the chart:
//   1-2 letters + digits with data-chart-codes (e.g. RA "A1"): exact chart code, so A1 isn't A10
//   digits only: shade numbers starting with them (leading zeros ignored), incl. the part after "-" in SKUs
//     and, except on RA, codes with a letter in front (310 finds Étoile C310, 130 finds Light Effects E130)
//   anything else: colour names containing it
// =====================================================================================================
(() => {
  if (customElements.get('colour-finder')) return;

  const stripZeros = (s) => s.replace(/\b0+(?=\d)/g, '');

  function matches(hay, t, chartCodes) {
    if (!t) return true;
    const words = hay.split(/\s+/);
    if (chartCodes && /^[a-t]\d{1,2}$/.test(t)) return words[0] === t;
    if (/^\d+$/.test(t)) {
      return words.some((w) => {
        const code = w.split('-').pop();
        return w.startsWith(t) || code.startsWith(t) || (!chartCodes && code.replace(/^[a-z]+/, '').startsWith(t));
      });
    }
    return hay.includes(t);
  }

  // Pence to "£1.25", in the shop's money format.
  const money = (pence) => ((window.theme && window.theme.moneyFormat) || '£{{amount}}')
    .replace(/\{\{\s*(\w+)\s*\}\}/, (m, k) => (k === 'amount_no_decimals' ? String(Math.round(pence / 100)) : (pence / 100).toFixed(2)));

  const cartUrl = () => (window.routes && window.routes.cartUrl) || '/cart';

  // Add lines to the basket, then tell the theme to refresh the basket count and mini-cart.
  function cartAdd(items) {
    const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    return fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items }),
    })
      .then((r) => r.json().then((d) => { if (!r.ok) throw d; return d; }))
      .then((d) => {
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        return d;
      });
  }

  // ---------------------------------------------------------------------------------------------------
  // <colour-finder>: picking a colour puts its id in the hidden option input and fires "change"; the
  // theme's variant picker then loads that version, as it does for its own swatches. The theme redraws
  // the whole block when it does that, so the search text and scroll position are kept in `saved` and
  // put back on the new copy.
  // ---------------------------------------------------------------------------------------------------
  const saved = {}; // per finder id: { q: search text, top: grid scroll position }

  class ColourFinder extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.input = this.querySelector('.colour-finder__input');
      this.grid = this.querySelector('.colour-finder__grid');
      this.items = [...this.querySelectorAll('.colour-finder__item')];
      this.field = this.querySelector('input[data-option-position]');
      this.selected = this.items.find((b) => b.classList.contains('is-selected')) || null;
      this.state = saved[this.id] || (saved[this.id] = { q: '', top: null });

      this.input.value = this.state.q;
      this.filter();
      if (this.state.top !== null) {
        this.grid.scrollTop = this.state.top;
      } else if (this.selected) {
        // First visit (or a ?variant= link): scroll the grid so the chosen colour is in view.
        const li = this.selected.parentNode;
        this.grid.scrollTop = li.offsetTop - (this.grid.clientHeight - li.offsetHeight) / 2;
      }
      this.preview(this.selected);

      this.input.addEventListener('input', () => {
        this.state.q = this.input.value;
        this.filter();
        this.grid.scrollTop = 0;
      });
      this.grid.addEventListener('scroll', () => { this.state.top = this.grid.scrollTop; }, { passive: true });
      this.grid.addEventListener('click', (e) => {
        const b = e.target.closest('.colour-finder__item');
        if (b) this.pick(b);
      });
      // The panel above the grid shows the chosen colour, or the one under the pointer / keyboard focus.
      const hover = (e) => {
        const b = e.target.closest('.colour-finder__item');
        if (b) this.preview(b);
      };
      this.grid.addEventListener('mouseover', hover);
      this.grid.addEventListener('focusin', hover);
      this.grid.addEventListener('mouseleave', () => this.preview(this.selected));
      this.grid.addEventListener('focusout', (e) => {
        if (!this.grid.contains(e.relatedTarget)) this.preview(this.selected);
      });
      this.grid.addEventListener('keydown', (e) => this.onKey(e));
    }

    get visible() {
      return this.items.filter((b) => !b.parentNode.hidden);
    }

    filter() {
      const t = stripZeros(this.input.value.toLowerCase().trim());
      const chartCodes = this.hasAttribute('data-chart-codes');
      let shown = 0;
      this.items.forEach((b) => {
        const ok = matches(stripZeros(b.dataset.search), t, chartCodes);
        b.parentNode.hidden = !ok;
        if (ok) shown++;
      });
      this.querySelector('.colour-finder__count').textContent = t
        ? `${shown} colour${shown === 1 ? '' : 's'} found`
        : `${this.items.length} colours`;
      this.querySelector('.colour-finder__none').hidden = shown > 0;
      this.setTabStop();
    }

    // Only one swatch is in the Tab order (the chosen one if it's showing); arrow keys move between them.
    setTabStop(target) {
      const visible = this.visible;
      const stop = target || (visible.includes(this.selected) ? this.selected : visible[0]);
      this.items.forEach((b) => { b.tabIndex = b === stop ? 0 : -1; });
    }

    onKey(e) {
      const visible = this.visible;
      const i = visible.indexOf(document.activeElement);
      if (i < 0) return;
      const cols = getComputedStyle(this.grid).gridTemplateColumns.split(' ').length;
      const steps = { ArrowRight: 1, ArrowLeft: -1, ArrowDown: cols, ArrowUp: -cols, Home: -i, End: visible.length };
      if (!(e.key in steps)) return;
      e.preventDefault();
      const next = visible[Math.max(0, Math.min(visible.length - 1, i + steps[e.key]))];
      this.setTabStop(next);
      next.focus();
    }

    preview(b) {
      const zoom = this.querySelector('.colour-finder__zoom');
      if (!b || !b.dataset.zoom) { zoom.hidden = true; return; }
      const prefix = this.dataset.prefix ? `${this.dataset.prefix} ` : '';
      const img = zoom.querySelector('.colour-finder__zoom-img');
      img.src = b.dataset.zoom;
      img.alt = `${this.dataset.brand} ${b.dataset.value}`;
      zoom.querySelector('.colour-finder__zoom-name').textContent = prefix + b.dataset.value;
      zoom.querySelector('.colour-finder__zoom-stock').textContent = b.dataset.stock;
      zoom.hidden = false;
    }

    pick(b) {
      if (b === this.selected) return;
      this.selected = b;
      this.items.forEach((i) => {
        i.classList.toggle('is-selected', i === b);
        i.setAttribute('aria-pressed', i === b);
      });
      this.setTabStop(b);
      this.preview(b);
      this.state.top = this.grid.scrollTop;
      this.field.value = b.dataset.valueId;
      this.field.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  // ---------------------------------------------------------------------------------------------------
  // <colour-chart>: data-mode="buy" opens a panel with price, quantity and Add to basket when a colour
  // is tapped; data-mode="link" lets the tap open the listing and previews the colour on hover. Chips
  // (data-group on each swatch's <li>) narrow the chart to one range or weight; data-remember keeps the
  // last chip in this browser for next time.
  // ---------------------------------------------------------------------------------------------------
  class ColourChart extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.input = this.querySelector('.colour-chart__input');
      if (!this.input) return; // just the pattern list on this page
      this.items = [...this.querySelectorAll('.colour-chart__item')];
      this.chips = [...this.querySelectorAll('.colour-chart__chip')];
      this.panel = this.querySelector('.colour-chart__panel');
      this.buying = this.dataset.mode === 'buy';
      this.selected = null;

      const grid = this.querySelector('.colour-chart__grid');
      this.input.addEventListener('input', () => this.filter());
      this.chips.forEach((chip) => chip.addEventListener('click', () => this.setGroup(chip.dataset.group, true)));
      grid.addEventListener('click', (e) => {
        const a = e.target.closest('.colour-chart__item');
        if (!a || !this.buying) return;
        e.preventDefault();
        this.select(a);
        // Bring the panel into view just below the sticky search bar (and the theme's sticky phone header).
        const bar = this.querySelector('.colour-chart__bar');
        this.panel.style.scrollMarginTop = `${(parseFloat(getComputedStyle(bar).top) || 0) + bar.offsetHeight + 12}px`;
        this.panel.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      });
      if (!this.buying) {
        const hover = (e) => {
          const a = e.target.closest('.colour-chart__item');
          if (a) this.show(a);
        };
        grid.addEventListener('mouseover', hover);
        grid.addEventListener('focusin', hover);
      }
      const add = this.querySelector('.colour-chart__add');
      if (add) add.addEventListener('click', () => this.add(add));
      this.setGroup(this.startGroup(), false);
    }

    // The chip to start on: the page's default, the one remembered from last time, or one named in the
    // address (e.g. /pages/appletons#crewel).
    startGroup() {
      const groups = this.chips.map((c) => c.dataset.group).filter((g) => g !== 'all');
      let group = this.dataset.group || 'all';
      if (this.dataset.remember) {
        try {
          const kept = localStorage.getItem(this.dataset.remember);
          if (groups.includes(kept)) group = kept;
        } catch (e) { /* storage blocked: use the default */ }
      }
      const hash = decodeURIComponent(location.hash).toLowerCase();
      const named = groups.find((g) => hash.includes(g.toLowerCase()));
      return named || group;
    }

    setGroup(group, chosen) {
      this.group = group;
      this.chips.forEach((c) => {
        const on = c.dataset.group === group;
        c.classList.toggle('is-on', on);
        c.setAttribute('aria-pressed', on);
      });
      // Appletons: the pattern list's wool weight follows the chips.
      this.querySelectorAll('input[name="colour-pattern-weight"]').forEach((r) => { r.checked = r.value === group; });
      if (chosen && this.dataset.remember) {
        try { localStorage.setItem(this.dataset.remember, group); } catch (e) { /* not kept, that's fine */ }
      }
      this.filter();
      if (this.selected && this.selected.parentNode.hidden) this.select(null);
    }

    filter() {
      const t = stripZeros(this.input.value.toLowerCase().trim());
      const chartCodes = this.hasAttribute('data-chart-codes');
      let shown = 0;
      let last = null;
      this.items.forEach((a) => {
        const li = a.parentNode;
        const ok = (this.group === 'all' || li.dataset.group === this.group) && matches(stripZeros(a.dataset.search), t, chartCodes);
        li.hidden = !ok;
        if (ok) { shown += 1; last = a; }
      });
      this.querySelector('.colour-chart__count').textContent = `${shown} colour${shown === 1 ? '' : 's'}${t ? ' found' : ''}`;
      this.querySelector('.colour-chart__none').hidden = shown > 0;
      // Link mode: a single match is previewed straight away, and clearing the search clears the preview.
      if (!this.buying) {
        if (t && shown === 1) this.show(last);
        else if (!t) this.show(null);
      }
    }

    select(a) {
      this.selected = a;
      this.items.forEach((i) => i.classList.toggle('is-selected', i === a));
      this.show(a);
    }

    // Fill the panel: price and Add to basket in buy mode, the listing it's in for link mode.
    show(a) {
      if (!a) { this.panel.hidden = true; return; }
      const ok = a.dataset.available === 'true';
      this.panel.querySelector('.colour-chart__panel-img').src = a.dataset.zoom || '';
      this.panel.querySelector('.colour-chart__panel-name').textContent = this.buying ? `${a.dataset.pname} ${a.dataset.name}` : a.dataset.name;
      this.panel.querySelector('.colour-chart__panel-detail').textContent = this.buying
        ? money(Number(a.dataset.cents)) + (this.dataset.priceSuffix || '')
        : `In the ${a.dataset.pname} listing`;
      this.panel.querySelector('.colour-chart__panel-stock').textContent = ok ? 'In stock' : 'Out of stock';
      const add = this.panel.querySelector('.colour-chart__add');
      if (add) {
        add.disabled = !ok;
        add.textContent = ok ? 'Add to basket' : 'Sold out';
        this.panel.querySelector('.colour-chart__msg').textContent = '';
      }
      this.panel.hidden = false;
    }

    add(button) {
      const a = this.selected;
      if (!a) return;
      const msg = this.querySelector('.colour-chart__msg');
      const quantity = Math.max(1, parseInt(this.querySelector('.colour-chart__qty').value, 10) || 1);
      button.disabled = true;
      msg.textContent = 'Adding…';
      cartAdd([{ id: Number(a.dataset.id), quantity }])
        .then(() => { msg.innerHTML = `Added to your basket. <a href="${cartUrl()}">View basket</a>`; })
        .catch((e) => { msg.textContent = (e && e.description) || 'Sorry, that could not be added.'; })
        .finally(() => { button.disabled = a.dataset.available !== 'true'; });
    }
  }

  // ---------------------------------------------------------------------------------------------------
  // <colour-pattern>: turns a pasted pattern key or typed list into one basket line per colour.
  // It knows every colour in the chart on the page plus the JSON inside it (listings the chart doesn't
  // show). Each colour is { s: sku, i: variant id, n: colour, r: listing name, g: group, p: pence,
  // a: in stock, t: thumbnail }. The two parsers below read a line the same way: a line with words
  // ("310 Black 2 skeins") takes its first code and any skein count; a line of codes ("310, 321 x2")
  // takes every code, with "x2" for quantities.
  // ---------------------------------------------------------------------------------------------------

  // DMC codes: 310, B5200, E3821, C321 (Étoile), D140, G168, 4510, blanc, ecru.
  function dmcPattern(colours) {
    const key = (sku) => {
      const i = sku.indexOf('-');
      const art = sku.slice(3, i);
      const code = sku.slice(i + 1).toLowerCase();
      if (art === '617' && (code === 'blanc' || code === 'ecru')) return `c${code}`; // Étoile blanc/ecru
      if (/^\d+$/.test(code)) return String(parseInt(code, 10));
      return code;
    };
    const idx = {};
    colours.forEach((c) => { idx[key(c.s)] = c; });
    const CODE = '(cblanc|cecru|blanc|ecru|b5200|[ecdg]\\d{2,4}|\\d{1,4})';
    const norm = (k) => (/^\d+$/.test(k) ? String(parseInt(k, 10)) : k);

    return (text) => {
      const s = text.toLowerCase().replace(/dmc/g, ' ').replace(/×/g, 'x')
        .replace(/c\s*-\s*(blanc|ecru)/g, 'c$1').replace(/\bwhite\b/g, 'blanc');
      const found = {};
      const order = [];
      const missing = [];
      const take = (code, q) => {
        const k = norm(code);
        if (idx[k]) {
          if (!found[k]) { found[k] = 0; order.push(k); }
          found[k] += q;
        } else if (!missing.includes(code.toUpperCase())) {
          missing.push(code.toUpperCase());
        }
      };
      s.split(/\n|;|,(?!\d{3}\b)/).forEach((part) => {
        const seg = part.trim();
        if (!seg) return;
        const words = seg.replace(/\b(cblanc|cecru|blanc|ecru|b5200|[ecdg]\d{2,4})\b/g, ' ').replace(/\bx\s*\d+/g, ' ').replace(/[^a-z]+/g, ' ').trim();
        if (words.length > 1) {
          const m = seg.match(new RegExp(`\\b${CODE}\\b`));
          if (!m) return;
          const mq = seg.match(/(\d+)\s*(?:skeins?|sk|reels?|pcs?)\b/) || seg.slice(m.index + m[0].length).match(/^\s*x\s*(\d{1,3})/);
          take(m[1], mq ? parseInt(mq[1], 10) || 1 : 1);
        } else {
          const re = new RegExp(`\\b${CODE}\\b(?:\\s*x\\s*(\\d{1,3}))?`, 'g');
          let m;
          while ((m = re.exec(seg))) take(m[1], m[2] ? parseInt(m[2], 10) : 1);
        }
      });
      return { lines: order.map((k) => ({ c: idx[k], q: found[k] })), missing };
    };
  }

  // Appletons shade numbers: 296, 291a. "crewel" or "tapestry" on a line beats the weight picked.
  function appletonsPattern(colours) {
    const idx = { tapestry: {}, crewel: {} };
    colours.forEach((c) => { if (idx[c.g]) idx[c.g][c.s.split('-').pop().toLowerCase()] = c; });
    const CODE = '([1-9]\\d{2}[ab]?)';

    return (text, weight) => {
      const s = text.toLowerCase().replace(/appletons?/g, ' ').replace(/×/g, 'x');
      const found = {};
      const order = [];
      const missing = [];
      const take = (code, q, w) => {
        if (idx[w][code]) {
          const k = `${w}:${code}`;
          if (!found[k]) { found[k] = 0; order.push(k); }
          found[k] += q;
        } else if (!missing.includes(code.toUpperCase())) {
          missing.push(code.toUpperCase());
        }
      };
      s.split(/\n|;|,/).forEach((part) => {
        const seg = part.trim();
        if (!seg) return;
        const w = /crewel/.test(seg) ? 'crewel' : (/tapestry/.test(seg) ? 'tapestry' : weight);
        const words = seg.replace(/\b[1-9]\d{2}[ab]?\b/g, ' ').replace(/\bx\s*\d+/g, ' ')
          .replace(/\b(crewel|tapestry|wool|ply|skeins?|hanks?|sk|m)\b/g, ' ').replace(/[^a-z]+/g, ' ').trim();
        if (words.length > 1) {
          const m = seg.match(new RegExp(`\\b${CODE}\\b`));
          if (!m) return;
          const mq = seg.match(/(\d+)\s*(?:skeins?|hanks?|sk|pcs?)\b/)
            || seg.slice(m.index + m[0].length).match(/^\s*x\s*(\d{1,3})/)
            || seg.slice(0, m.index).match(/(\d{1,2})\s*x\s*$/);
          take(m[1], mq ? parseInt(mq[1], 10) || 1 : 1, w);
        } else {
          const re = new RegExp(`(?:(\\d{1,2})\\s*x\\s*)?\\b${CODE}\\b(?:\\s*x\\s*(\\d{1,3}))?`, 'g');
          let m;
          while ((m = re.exec(seg))) take(m[2], m[3] ? parseInt(m[3], 10) : (m[1] ? parseInt(m[1], 10) : 1), w);
        }
      });
      return {
        lines: order.map((k) => {
          const [w, code] = k.split(':');
          return { c: idx[w][code], q: found[k] };
        }),
        missing,
      };
    };
  }

  class ColourPattern extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      const chart = this.closest('colour-chart');
      const colours = chart ? [...chart.querySelectorAll('.colour-chart__item')].map((el) => ({
        s: el.dataset.sku,
        i: Number(el.dataset.id),
        n: el.dataset.name,
        r: el.dataset.pname,
        g: el.parentNode.dataset.group,
        p: Number(el.dataset.cents),
        a: el.dataset.available === 'true',
        t: el.querySelector('img') ? el.querySelector('img').getAttribute('src') : '',
      })) : [];
      try {
        colours.push(...JSON.parse(this.querySelector('.colour-pattern__data').textContent));
      } catch (e) { /* no extra listings */ }
      this.parse = (this.dataset.brand === 'appletons' ? appletonsPattern : dmcPattern)(colours);

      this.input = this.querySelector('.colour-pattern__input');
      this.out = this.querySelector('.colour-pattern__out');
      this.addAll = this.querySelector('.colour-pattern__addall');
      this.rows = [];
      this.querySelector('.colour-pattern__go').addEventListener('click', () => this.run());
      this.querySelectorAll('input[name="colour-pattern-weight"]').forEach((r) => {
        r.addEventListener('change', () => { if (!this.out.hidden) this.run(); });
      });
      this.addAll.addEventListener('click', () => this.addAllToBasket());
    }

    run() {
      if (!this.input.value.trim()) return;
      const r = this.querySelector('input[name="colour-pattern-weight"]:checked');
      this.render(this.parse(this.input.value, r ? r.value : 'tapestry'));
    }

    render(res) {
      const list = this.querySelector('.colour-pattern__list');
      const missing = this.querySelector('.colour-pattern__missing');
      list.innerHTML = '';
      this.rows = [];
      this.querySelector('.colour-pattern__msg').textContent = '';
      res.lines.forEach(({ c, q }) => {
        const li = document.createElement('li');
        li.className = `colour-pattern__row${c.a ? '' : ' is-sold'}`;
        li.innerHTML = '<img alt="" loading="lazy"><span class="colour-pattern__name"></span>'
          + '<input class="colour-pattern__qty" type="number" min="0" inputmode="numeric" aria-label="Quantity">'
          + '<span class="colour-pattern__line"></span><button type="button" class="colour-pattern__rm" aria-label="Remove">×</button>';
        if (c.t) li.querySelector('img').src = c.t;
        const name = li.querySelector('.colour-pattern__name');
        name.textContent = c.n;
        const small = document.createElement('small');
        small.textContent = `${c.r} · ${c.a ? `${money(c.p)} each` : 'out of stock'}`;
        name.appendChild(small);
        // Sold-out colours stay on the list so the shopper knows, but aren't added to the basket.
        const qty = li.querySelector('.colour-pattern__qty');
        qty.value = c.a ? q : 0;
        qty.disabled = !c.a;
        const row = { c, qty, line: li.querySelector('.colour-pattern__line') };
        this.rows.push(row);
        qty.addEventListener('input', () => this.total());
        li.querySelector('.colour-pattern__rm').addEventListener('click', () => {
          this.rows.splice(this.rows.indexOf(row), 1);
          li.remove();
          this.total();
        });
        list.appendChild(li);
      });
      missing.hidden = !res.missing.length;
      missing.textContent = res.missing.length ? `Not found: ${res.missing.join(', ')}. Please check these numbers.` : '';
      this.out.hidden = false;
      this.total();
    }

    total() {
      let pence = 0;
      let count = 0;
      this.rows.forEach((r) => {
        const q = Math.max(0, parseInt(r.qty.value, 10) || 0);
        pence += q * r.c.p;
        count += q;
        r.line.textContent = r.c.a ? money(q * r.c.p) : 'Sold out';
      });
      this.querySelector('.colour-pattern__total').textContent = `${count} skein${count === 1 ? '' : 's'} · ${money(pence)}`;
      this.addAll.disabled = count === 0;
      this.addAll.textContent = count ? `Add all ${count} to basket` : 'Add all to basket';
    }

    addAllToBasket() {
      const items = this.rows
        .map((r) => ({ id: r.c.i, quantity: Math.max(0, parseInt(r.qty.value, 10) || 0), properties: { '_Added via': 'Pattern list' } }))
        .filter((x) => x.quantity > 0);
      if (!items.length) return;
      const msg = this.querySelector('.colour-pattern__msg');
      this.addAll.disabled = true;
      msg.textContent = 'Adding…';
      cartAdd(items)
        .then(() => { msg.innerHTML = `All added to your basket. <a href="${cartUrl()}">View basket</a>`; })
        .catch((e) => { msg.textContent = (e && e.description) || 'Sorry, those could not be added. Please try again.'; })
        .finally(() => { this.addAll.disabled = false; });
    }
  }

  customElements.define('colour-finder', ColourFinder);
  customElements.define('colour-chart', ColourChart);
  customElements.define('colour-pattern', ColourPattern);
})();
